import { ToastProvider } from '@enter/core';
import { InactivityDetector, ProviderMessagingProvider } from 'components';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { RestrictedRoute } from 'router';
import { DialogProvider } from 'services';
import { IInjectedRootStore } from 'stores';
import { ChargemasterDetail, ChargemasterListing } from './chargemasters';
import { ByFilter, ClaimDetail, ClaimListing } from './claims';
import { Dashboard } from './dashboard';
import { Documents } from './documents';
import { Eligibility } from './eligibility';
import { Fax } from './fax';
import { GuarantorDetails, Guarantors } from './guarantors';
import { Mail } from './mail';
import { MainSidebar } from './main.sidebar';
import * as S from './main.styles';
import { MainTopnav } from './main.topnav';
import { Messaging } from './messaging';
import {PatientStatement} from "./patients";
import { Payments } from './payments';
import { Overpayments } from './overpayments';
import { Reports } from './reports';
import { Settings } from './settings';
import { Settlements } from './settlements';
import { Transactions } from './transactions';
import {Phone} from "./phone";

type IMainProps = IInjectedRootStore & RouteComponentProps;

export const Main: React.FunctionComponent<IMainProps> = inject('rootStore')(
    observer(
        (props: IMainProps): React.ReactElement => {
            const { rootStore } = props;

            const renderNotFound = (): React.ReactElement => <Redirect to="/404" />;

            return (
                <ToastProvider>
                    <DialogProvider>
                        <ProviderMessagingProvider>
                            <MainSidebar rootStore={rootStore} />
                            <MainTopnav rootStore={rootStore} />
                            <S.Main sidebarOpened={!rootStore.layoutStore.sidebarCollapsed}>
                                <Switch>
                                    <Route path="/" component={Dashboard} exact={true} />
                                    <Route path="/claims" component={ClaimListing} exact={true} />
                                    <Route path="/claims/:claimId" component={ClaimDetail} exact={true} />
                                    <Route path="/claims/by-:filterType/:id" component={ByFilter} exact={true} />
                                    <Route path="/messaging" component={Messaging} />
                                    <Route path="/eligibility" component={Eligibility} exact={true} />
                                    <Route path="/reports" component={Reports} exact={true} />
                                    <Route path="/settings" component={Settings} exact={false} />
                                    <Route path="/chargemasters" component={ChargemasterListing} exact={true} />
                                    <Route
                                        path="/chargemasters/:chargemasterId"
                                        component={ChargemasterDetail}
                                        exact={true}
                                    />
                                    <Route path="/payments" component={Payments} exact={true} />
                                    <Route path="/overpayments" component={Overpayments} exact={true} />

                                    <Route path="/overpayments" exact={true}>
                                        <Redirect to="/overpayments/pending" />
                                    </Route>
                                    <Route path="/overpayments/:tab(pending|approved|submitted|completed)" component={Overpayments} exact={true} />

                                    <Route path="/patients/:patientId/statement" component={PatientStatement} exact={true} />
                                    <Route path="/documents" component={Documents} exact={true} />
                                    <Route path="/guarantors" component={Guarantors} exact={true} />
                                    <Route path="/guarantors/:id" component={GuarantorDetails} exact={true} />
                                    <Route path="/transactions" component={Transactions} exact={true} />
                                    <Route exact={true} path="/mail">
                                        <Redirect to="/mail/inbox" />
                                    </Route>
                                    <Route path="/mail/:tab(inbox|outbox|shipment)" component={Mail} exact={true} />
                                    <Route path="/phone" component={Phone} exact={true}/>

                                    <Route exact={true} path="/fax">
                                        <Redirect to="/fax/inbox" />
                                    </Route>
                                    <Route path="/fax/:tab(inbox|outbox)" component={Fax} exact={true} />

                                    {/* <RestrictedRoute requires="DEV" path="/mail" component={Mail} exact={true} /> */}
                                    <RestrictedRoute
                                        requires="ADMIN"
                                        path="/settlements"
                                        component={Settlements}
                                        exact={true}
                                    />
                                    <Route render={renderNotFound} />
                                </Switch>
                            </S.Main>
                            {!rootStore.isLocal() && <InactivityDetector />}
                        </ProviderMessagingProvider>
                    </DialogProvider>
                </ToastProvider>
            );
        },
    ),
);
